import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { IFilterAngularComp } from 'ag-grid-angular';
import { IDoesFilterPassParams, IFilterParams } from 'ag-grid-community';

@Component({
  selector: 'app-datetime-filter',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './datetime-filter.component.html',
  styleUrl: './datetime-filter.component.scss',
})
export class DatetimeFilterComponent implements IFilterAngularComp {
  private params!: IFilterParams;

  filterType = 'equals';
  date = '';
  time = '';
  dateTo = '';
  timeTo = '';

  readonly filterOptions = [
    { value: 'equals', label: 'Equals' },
    { value: 'greaterThan', label: 'After' },
    { value: 'lessThan', label: 'Before' },
    { value: 'inRange', label: 'Between' },
  ];

  agInit(params: IFilterParams): void {
    this.params = params;
  }

  isFilterActive(): boolean {
    return !!this.date;
  }

  doesFilterPass(params: IDoesFilterPassParams): boolean {
    const field = this.params.colDef.field;
    if (!field || !this.date) return true;

    const cellValue = params.data[field];
    if (!cellValue) return false;

    const cellTime = new Date(cellValue).getTime();
    const fromTime = this.buildDateTime(this.date, this.time).getTime();

    switch (this.filterType) {
      case 'equals': {
        // Compare to the minute
        const cellMinute = Math.floor(cellTime / 60000);
        const filterMinute = Math.floor(fromTime / 60000);
        return cellMinute === filterMinute;
      }
      case 'greaterThan':
        return cellTime >= fromTime;
      case 'lessThan':
        return cellTime <= fromTime;
      case 'inRange': {
        if (!this.dateTo) return cellTime >= fromTime;
        const toTime = this.buildDateTime(this.dateTo, this.timeTo).getTime();
        return cellTime >= fromTime && cellTime <= toTime;
      }
      default:
        return true;
    }
  }

  getModel(): any {
    if (!this.isFilterActive()) return null;

    const dateValue = this.formatDateTime(this.date, this.time);

    if (this.filterType === 'inRange') {
      return {
        filterType: 'date',
        type: this.filterType,
        dateFrom: dateValue,
        dateTo: this.dateTo ? this.formatDateTime(this.dateTo, this.timeTo) : null,
      };
    }

    return {
      filterType: 'date',
      type: this.filterType,
      filter: dateValue,
    };
  }

  setModel(model: any): void {
    if (model) {
      this.filterType = model.type || 'equals';
      const fromStr = model.filter || model.dateFrom;
      if (fromStr) {
        const from = new Date(fromStr.replace(' ', 'T'));
        this.date = this.toDateInput(from);
        this.time = this.toTimeInput(from);
      }
      if (model.dateTo) {
        const to = new Date(model.dateTo.replace(' ', 'T'));
        this.dateTo = this.toDateInput(to);
        this.timeTo = this.toTimeInput(to);
      }
    } else {
      this.clearFilter();
    }
  }

  applyFilter(): void {
    this.params.filterChangedCallback();
  }

  clearFilter(): void {
    this.filterType = 'equals';
    this.date = '';
    this.time = '';
    this.dateTo = '';
    this.timeTo = '';
    this.params.filterChangedCallback();
  }

  private buildDateTime(date: string, time: string): Date {
    const [year, month, day] = date.split('-').map(Number);
    if (time) {
      const [hours, minutes] = time.split(':').map(Number);
      return new Date(year, month - 1, day, hours, minutes, 0);
    }
    return new Date(year, month - 1, day, 0, 0, 0);
  }

  private formatDateTime(date: string, time: string): string {
    const d = this.buildDateTime(date, time);
    const pad = (n: number) => n.toString().padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:00`;
  }

  private toDateInput(d: Date): string {
    const pad = (n: number) => n.toString().padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  }

  private toTimeInput(d: Date): string {
    const pad = (n: number) => n.toString().padStart(2, '0');
    return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }
}
